import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { GoldCoin } from "@/components/CoinIcons";
import { 
  TrendingUp, 
  TrendingDown, 
  ChevronLeft, 
  ChevronRight,
  Calendar
} from "lucide-react";

export default function GoldHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({});
  const [summary, setSummary] = useState({});
  const [currentBalance, setCurrentBalance] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadTransactions(page);
  }, [page]);

  const loadTransactions = async (pageNum) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/gold/transactions?page=${pageNum}&per_page=50`);
      setTransactions(data.transactions);
      setPagination(data.pagination);
      setSummary(data.summary);
      setCurrentBalance(data.current_balance);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceIcon = (source) => {
    // Map source to appropriate icon/emoji
    const iconMap = {
      'arena_win': '⚔️',
      'arena_victory': '⚔️',
      'merchant_sale': '🏪',
      'equipment_sale': '⚒️',
      'quest_reward': '📜',
      'daily_reward': '🎁',
      'alliance_reward': '🛡️',
      'campaign_reward': '🏛️',
      'boss_reward': '👹',
      'tournament_prize': '🏆',
      'referral_bonus': '👥',
      'vip_purchase': '👑',
      'equipment_purchase': '⚒️',
      'training_cost': '💪',
      'recruitment': '👤',
      'medical_treatment': '⚕️',
      'auction_bid': '🔨',
      'default': 'gold-coin'
    };
    return iconMap[source] || iconMap['default'];
  };

  const formatSource = (source) => {
    return source
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading && transactions.length === 0) {
    return <Loader label="Loading gold history..." />;
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl">
      <PageHeader
        title="Gold Transaction History"
        subtitle="Last 14 days of all gold income and expenses"
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Current Balance */}
        <div className="bg-gradient-to-br from-amber-950/40 to-zinc-900/40 border border-amber-700/30 rounded p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-400 uppercase tracking-wide mb-2">
            <GoldCoin size={14} />
            <span>Current Balance</span>
          </div>
          <div className="text-2xl font-cinzel text-amber-300">
            {currentBalance.toLocaleString()}
          </div>
        </div>

        {/* Total Income */}
        <div className="bg-gradient-to-br from-emerald-950/30 to-zinc-900/40 border border-emerald-700/30 rounded p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-400 uppercase tracking-wide mb-2">
            <TrendingUp size={14} className="text-emerald-400" />
            <span>Total Income</span>
          </div>
          <div className="text-2xl font-cinzel text-emerald-300">
            +{summary.total_income?.toLocaleString() || 0}
          </div>
        </div>

        {/* Total Expenses */}
        <div className="bg-gradient-to-br from-red-950/30 to-zinc-900/40 border border-red-700/30 rounded p-4">
          <div className="flex items-center gap-2 text-xs text-zinc-400 uppercase tracking-wide mb-2">
            <TrendingDown size={14} className="text-red-400" />
            <span>Total Expenses</span>
          </div>
          <div className="text-2xl font-cinzel text-red-300">
            -{summary.total_expenses?.toLocaleString() || 0}
          </div>
        </div>
      </div>

      {/* Transaction List */}
      {transactions.length === 0 ? (
        <Empty label="No Transactions">
          <p className="text-sm text-zinc-400">
            No gold transactions in the last 14 days
          </p>
        </Empty>
      ) : (
        <>
          <div className="bg-zinc-900/50 border border-amber-900/30 rounded overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800/50 border-b border-amber-900/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-cinzel uppercase tracking-wider text-amber-400">
                    <Calendar size={14} className="inline mr-2" />
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-cinzel uppercase tracking-wider text-amber-400">
                    Transaction
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-cinzel uppercase tracking-wider text-amber-400">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-cinzel uppercase tracking-wider text-amber-400">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/50">
                {transactions.map((tx, idx) => (
                  <tr key={idx} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-zinc-400" title={formatFullDate(tx.timestamp)}>
                      {formatDate(tx.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getSourceIcon(tx.source) === 'gold-coin' ? (
                          <GoldCoin size={18} />
                        ) : (
                          <span className="text-lg">{getSourceIcon(tx.source)}</span>
                        )}
                        <span className="text-sm text-zinc-300 font-medium">
                          {formatSource(tx.source)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-400">
                      {tx.description}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-cinzel font-semibold ${tx.type === 'income' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {tx.type === 'income' ? '+' : '-'}{tx.amount.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-zinc-400">
                Page {pagination.page} of {pagination.total_pages} 
                <span className="ml-2 text-zinc-500">
                  ({pagination.total} transactions)
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`${btn()} text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                  disabled={page === pagination.total_pages}
                  className={`${btn()} text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
